import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { take, takeUntil } from 'rxjs/operators';
import { ProjectCommunicationService } from '../../../../communication/project-comms.service';
import { ConfiguredToastrService } from '../../../../helper/toastr/configured-toastr.service';
import { AnessaTranslationService } from '../../../../helper/translation/anessa-translation.service';
import { UserpilotReloader } from '../../../../helper/userpilot-reloader.component';
import { SectionType } from '../../../../models/enums/checklist/checklist-section-type.enum';
import { ProjectChecklist, ProjectChecklistColumn, ProjectChecklistRow } from '../../../../models/project/project-info.model';
import Utils from '../../../../util';
import { ProjectChecklistService } from '../checklist.service';
import { SectionFormService } from './checklist-section-form.service';

@Component({
  selector: 'app-project-info-checklist-form',
  templateUrl: './checklist-form.component.html',
  styleUrls: [
    './checklist-form.component.scss',
  ],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})
export class ProjectInfoChecklistFormComponent extends UserpilotReloader implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('tabGroup') tabGroup: MatTabGroup;
  onDestroyNotifier = new EventEmitter();

  _checklists: ProjectChecklist[] = [];
  @Input() set checklists(checklists: ProjectChecklist[]) {
    this._checklists = checklists;
    this.updateDataObjects();
  }
  @Output() checklistsChange: EventEmitter<ProjectChecklist[]> = new EventEmitter();

  translationTrigger: string;

  sectionTitles: string[][] = [];
  /**
   * Holds which section is the first feedstock.
   * @example
   *  { checklist: { firstFeed?, firstFeed?, ... }, ...}
   *  { 0: { 0: false, 1: true, 2: false }, ... }
   */
  sectionIsFirstFeedstock = {};

  isInitialFormUser = false;

  editInProgress = false;

  allowTabChangeLogic = true;

  constructor(
    private checklistService: ProjectChecklistService,
    private sectionFormService: SectionFormService,
    private toastrService: ConfiguredToastrService,
    private translationService: AnessaTranslationService,
    private projectCommService: ProjectCommunicationService,
  ) {
    super();
  }

  ngOnInit() {
    if (!this._checklists.length) {
      this.checklistService.checklistsSavedNotifier
        .pipe(takeUntil(this.onDestroyNotifier))
        .subscribe(saved => {
          if (saved) {
            this._checklists = this.checklistService.getChecklists();
            this.updateDataObjects();
          }
        });

      this.checklistService.checklistsLoadedNotifier
        .pipe(takeUntil(this.onDestroyNotifier))
        .subscribe(saved => {
          if (saved) {
            this._checklists = this.checklistService.getChecklists();
            this.updateDataObjects();
          }
        });

      this.checklistService.checklistsModifiedNotifier
        .pipe(takeUntil(this.onDestroyNotifier))
        .subscribe(saved => {
          if (saved) {
            this._checklists = this.checklistService.getChecklists();
            this.updateDataObjects();
          }
        });

      this.checklistService.checklistsConvertedNotifier
        .pipe(takeUntil(this.onDestroyNotifier))
        .subscribe(() => {
          this._checklists = this.checklistService.getChecklists();
          this.updateDataObjects();
          this.attachTabClickEvent();
        });

    }

    this.checklistService.checklistFormTemplateLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this._checklists = this.checklistService.getChecklists();
        this.editInProgress = false;
        this.updateDataObjects();
        this.attachTabClickEvent();
      });

    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe((language: string) => {
        this.translationTrigger = language;
      });

    this.projectCommService.projectInputDraftSaveNotifier
      .pipe(takeUntil(this.onDestroyNotifier.asObservable()))
      .subscribe(save => {
        if (save === true) {
          this.editInProgress = false;
          try {
            this.onSave(false);
            this.checklistService.formSavedToChecklistNotifier.next(true);
          } catch (error) {
            this.toastrService.error(error.message);
            this.projectCommService.projectInputDraftSaveNotifier.next(false);
          }
        }
      });

    this.sectionFormService.saveForm$
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.onSave(false);
        this.checklistService.formSavedToChecklistNotifier.next(true);
      });

    if (Utils.isInitialFormUser()) {
      this.isInitialFormUser = true;
    }
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit(true);
    this.onDestroyNotifier.unsubscribe();
  }

  ngAfterViewInit() {
    this.attachTabClickEvent();
  }

  attachTabClickEvent() {
    setTimeout(() => {
      // Because this is in a setTimeout, when checklistFormTemplateLoadedNotifier emits
      // this component is sometimes destroyed before this logic runs. So make sure the 
      // onDestroy notifier hasn't completed yet or you will get an ObjectUnsubscribedError.
      if (this.tabGroup && !this.onDestroyNotifier.closed) {
        this.tabGroup._handleClick = (tab, tabHeader, index) => this.tabGroup.focusTab(index);
        this.tabGroup.focusChange
          .pipe(takeUntil(this.onDestroyNotifier))
          .subscribe(change => {
            if (this.allowTabChangeLogic) {
              this.allowTabChangeLogic = false;
              this.handleTabChange(change.tab, this.tabGroup._tabHeader, change.index)
            }
          });
      }
    });
  }

  async handleTabChange(tab: MatTab, tabHeader, index: number) {
    if (this.editInProgress && this.tabGroup.selectedIndex != index) {
      this.sectionFormService.allowPageLeave$
        .pipe(take(1))
        .subscribe(leave => {
          if (leave) {
            this.editInProgress = false;
          } else {
            this.tabGroup.focusTab(this.tabGroup.selectedIndex);
          }
          this.allowTabChangeLogic = true;
          return leave && MatTabGroup.prototype._handleClick.apply(this.tabGroup, [tab, tabHeader, index]);
        });
      this.sectionFormService.notifyCancel();
    } else {
      await this.delay(50);
      this.allowTabChangeLogic = true;
      return MatTabGroup.prototype._handleClick.apply(this.tabGroup, [tab, tabHeader, index]);
    }
  }

  private delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  onSave(notify = true) {
    this.checklistService.editChecklists(this._checklists, notify);
    this.checklistsChange.emit(this._checklists);
  }

  editCancel(listIndex, rowIndex, event: { row, columns, timeout }) {
    this.editInProgress = false;
    const checklist = this.getChecklist(listIndex);
    checklist.rows.splice(rowIndex, 1, event.row);
    checklist.columns = event.columns;

    setTimeout(() => {
      // because of the splice, causes the view to refresh, splitting section component would fix this
      // use a variable to set delay based on if its a button click or tab change
      this._checklists.splice(listIndex, 1, checklist);
      this._checklists = [...this._checklists];
      this.updateDataObjects();
    }, event.timeout);
  }

  addSection(listIndex, rowIndex, event: { row, columns }) {
    this.editInProgress = false;
    const checklist = this.getChecklist(listIndex);
    checklist.rows.splice(rowIndex + 1, 0, event.row);
    checklist.columns = event.columns;

    this._checklists.splice(listIndex, 1, checklist);
    this._checklists = [...this._checklists];
    this.updateDataObjects();
    this.onSave(false);

    setTimeout(() => {
      if (this.tabGroup) {
        this.tabGroup.selectedIndex = this.tabGroup.selectedIndex + 1;
      }
    });
  }

  removeSection(listIndex, rowIndex, columns: ProjectChecklistColumn[]) {
    this.editInProgress = false;
    const checklist = this.getChecklist(listIndex);
    checklist.rows.splice(rowIndex, 1);
    checklist.columns = columns;

    this._checklists.splice(listIndex, 1, checklist);
    this._checklists = [...this._checklists];
    this.updateDataObjects();
    this.onSave(false);
  }

  saveSection(listIndex, rowIndex, event: { row: ProjectChecklistRow, columns: ProjectChecklistColumn[] }) {
    this.editInProgress = false;

    const checklist = this.getChecklist(listIndex);
    checklist.rows.splice(rowIndex, 1, event.row);
    this.updateOtherSections(checklist, event.columns);
    checklist.columns = event.columns;

    this._checklists.splice(listIndex, 1, checklist);
    this._checklists = [...this._checklists];

    this.updateDataObjects();
    this.onSave(false);
  }

  private updateOtherSections(checklist: ProjectChecklist, columns: ProjectChecklistColumn[]) {
    const maxLevelShown = this.getMaxLevel(checklist);
    this.flattenChecklistRows(checklist.rows, maxLevelShown).forEach(row => {
      for (const col of columns) {
        const originalName = checklist.columns.find(c => c.uuid === col.uuid)?.name;
        if (originalName !== col.name && originalName !== undefined && !row.data[col.name]) {
          row.data[col.name] = row.data[originalName];
          delete row.data[originalName];
        } else if (originalName === undefined && !row.data[col.name]) {
          row.data[col.name] = null;
        }
      }
    });
  }

  private flattenChecklistRows(rows: ProjectChecklistRow[], maxLevel: number, parentIndex?: string) {
    const flattenedRows = [];
    rows.forEach((row, index) => {
      let dotIndex: string;
      if (parentIndex) {
        dotIndex = `${parentIndex}.${String(index + 1)}`;
      } else {
        dotIndex = String(index + 1);
      }
      row.data.id = dotIndex;
      flattenedRows.push(row);
      // Only include rows up to the overall selected level
      if (row.level < maxLevel) {
        flattenedRows.push(...this.flattenChecklistRows(row.children, maxLevel, dotIndex));
      }
    });
    return flattenedRows;
  }

  private getMaxLevel(checklist: ProjectChecklist) {
    let maxLevel = 0;
    const recFunc = (rows: ProjectChecklistRow[]) => {
      for (const row of rows) {
        if (row.level > maxLevel) {
          maxLevel = row.level;
        }
        if (row.children.length > 0) {
          recFunc(row.children);
        }
      }
    };
    recFunc(checklist.rows);
    return maxLevel;
  }

  getChecklist(index: number): ProjectChecklist {
    if (index > -1 && index < this._checklists.length) {
      return ProjectChecklist.deepCopy(this._checklists[index]);
    }
    return null;
  }

  updateDataObjects() {
    this.setSectionTitles();
    this.setIsFirstFeedstockSectionData();
  }

  setIsFirstFeedstockSectionData() {
    this.sectionIsFirstFeedstock = {};
    for (let listIndex = 0; listIndex < this._checklists.length; listIndex++) {
      const list = this._checklists[listIndex];
      this.sectionIsFirstFeedstock[listIndex] = {};
      let firstFeedFound = false;
      for (let rowIndex = 0; rowIndex < list.rows.length; rowIndex++) {
        const row = list.rows[rowIndex];
        if (firstFeedFound) {
          this.sectionIsFirstFeedstock[listIndex][rowIndex] = false;
        } else if (row.sectionType === SectionType.FEEDSTOCK) {
          this.sectionIsFirstFeedstock[listIndex][rowIndex] = true;
          firstFeedFound = true;
        }
      }
    }
  }

  setSectionTitles() {
    this.sectionTitles = [];

    let getSectionName = (section: ProjectChecklistRow, checklist: ProjectChecklist) => {
      if (checklist.columns.length > 0) {
        let sectionName = section.data[checklist.columns[0].name]?.trim();
        if (!sectionName) {
          return this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.SECTION');
        } else {
          return sectionName;
        }
      }
    };

    for (const checklist of this._checklists) {
      let titles: string[] = [];
      let counter = 1;
      for (const section of checklist.rows) {
        if (section.sectionType === SectionType.FEEDSTOCK) {
          titles.push(counter + '. ' + getSectionName(section, checklist));
          counter++;
        } else {
          titles.push(getSectionName(section, checklist));
        }
      }
      this.sectionTitles.push(titles);
    }
    this.sectionTitles = [...this.sectionTitles];
  }
}
